import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DataProvider, useData } from './contexts/DataContext';
import { ThemeProvider } from '@flowcore-water/ui';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoginPage } from './components/LoginPage';
import { SkillTree } from './components/SkillTree';
import { Dashboard } from './components/Dashboard';
import { WaterWellTechTraining } from './components/WaterWellTechTraining';
import { UserManagement } from './components/UserManagement';
import { usePageTitle, useTheme } from '@flowcore-water/ui';

import { LibraryPage } from './components/library/LibraryPage';
import { LibraryItemView } from './components/library/LibraryItemView';
import { LibraryItemEditor } from './components/library/LibraryItemEditor';
import { AttestationList } from './components/attestation/AttestationList';
import { AttestationDashboard } from './components/attestation/AttestationDashboard';
// EnrollmentManager removed — enrollment management is now in Dashboard
import { SkillTreeConfig } from './components/SkillTreeConfig';
import { MonitoringDashboard } from './components/MonitoringDashboard';
import { StudentTimeline } from './components/timeline/StudentTimeline';
import { enrollmentService } from './services/enrollmentService';
import { monitoring } from './services/monitoringService';
import { Sentry } from './instrument';
import { SKILL_TREES } from './data/skillTrees';
import type { Enrollment } from './types';
import { seedDemoDB } from './utils/seedDev';
import { AlertBanner } from '@flowcore-water/ui';

const AppRoutes: React.FC = () => {
  const { t } = useTheme();
  const { user, loading: authLoading } = useAuth();

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="" style={{ color: t.textMuted }}>Loading...</div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={user ? <Navigate to="/tree" replace /> : <LoginPage />}
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Navigate to="/tree" replace />
          </ProtectedRoute>
        }
      />

      {/* Personal Skill Tree */}
      <Route
        path="/tree"
        element={
          <ProtectedRoute>
            <MyTreeRoute />
          </ProtectedRoute>
        }
      />

      {/* Student Skill Tree (Instructor View) */}
      <Route
        path="/tree/:userId"
        element={
          <ProtectedRoute requiredRole={['instructor', 'admin']}>
            <TreeViewRoute />
          </ProtectedRoute>
        }
      />

      {/* Dashboard */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requiredRole={['instructor', 'admin']}>
            <DashboardRoute />
          </ProtectedRoute>
        }
      />

      {/* Water Well Technician Training */}
      <Route
        path="/water-well-technician-training"
        element={
          <ProtectedRoute requiredRole={['instructor', 'admin']}>
            <WaterWellTechTrainingRoute />
          </ProtectedRoute>
        }
      />

      {/* User Management */}
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requiredRole={['admin']}>
            <UserManagementRoute />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/skill-trees"
        element={
          <ProtectedRoute requiredRole={['admin']}>
            <SkillTreeConfigRoute />
          </ProtectedRoute>
        }
      />

      {/* Library Routes */}
      <Route
        path="/library"
        element={
          <ProtectedRoute>
            <LibraryListRoute />
          </ProtectedRoute>
        }
      />

      <Route
        path="/library/new"
        element={
          <ProtectedRoute requiredRole={['instructor', 'admin']}>
            <LibraryEditorRoute />
          </ProtectedRoute>
        }
      />

      <Route
        path="/library/edit/:id"
        element={
          <ProtectedRoute requiredRole={['instructor', 'admin']}>
            <LibraryEditorRoute />
          </ProtectedRoute>
        }
      />

      <Route
        path="/library/:slug"
        element={
          <ProtectedRoute>
            <LibraryViewRoute />
          </ProtectedRoute>
        }
      />

      {/* Legacy wiki redirects */}
      <Route path="/wiki" element={<Navigate to="/library" replace />} />
      <Route path="/wiki/*" element={<Navigate to="/library" replace />} />

      {/* Attestation Routes */}
      <Route
        path="/attestations"
        element={
          <ProtectedRoute>
            <AttestationsRoute />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/attestations"
        element={
          <ProtectedRoute requiredRole={['admin']}>
            <AttestationsDashboardRoute />
          </ProtectedRoute>
        }
      />

      {/* Monitoring Dashboard */}
      <Route
        path="/admin/monitoring"
        element={
          <ProtectedRoute requiredRole={['admin']}>
            <MonitoringRoute />
          </ProtectedRoute>
        }
      />

      {/* Timeline Routes */}
      <Route
        path="/timeline"
        element={
          <ProtectedRoute>
            <TimelineRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="/timeline/:userId"
        element={
          <ProtectedRoute requiredRole={['instructor', 'admin']}>
            <TimelineRoute />
          </ProtectedRoute>
        }
      />

      {/* 404 */}
      <Route path="*" element={<Navigate to="/tree" replace />} />
    </Routes>
  );
};

/** Tabs for switching between enrolled skill trees */
const SkillTreeTabs: React.FC<{
  enrollments: Enrollment[];
  activeTreeId: string | null;
  onSelect: (treeId: string) => void;
}> = ({ enrollments, activeTreeId, onSelect }) => {
  return (
    <div className="flex gap-1 rounded-lg p-1" style={{ background: 'rgba(0,0,0,0.25)' }}>
      {enrollments.map((enr) => {
        const tree = SKILL_TREES.find((tr) => tr.id === enr.skillTreeId);
        const isActive = enr.skillTreeId === activeTreeId;
        return (
          <button
            key={enr.id}
            onClick={() => onSelect(enr.skillTreeId)}
            className="px-4 py-2 rounded-md text-sm font-medium transition-all"
            style={{
              background: isActive ? 'rgba(34, 211, 238, 0.12)' : 'transparent',
              color: isActive ? '#22d3ee' : '#a1b1c7',
              border: isActive ? '1px solid rgba(34, 211, 238, 0.3)' : '1px solid transparent',
            }}
          >
            {tree?.shortName || enr.skillTreeId}
          </button>
        );
      })}
    </div>
  );
};

const MyTreeRoute: React.FC = () => {
  usePageTitle('Training', 'My Skills');
  const { t } = useTheme();
  const { user } = useAuth();
  const { qualifications, userProgress, updateProgress } = useData(user?.uid);
  const canEdit = user?.role === 'instructor' || user?.role === 'admin';
  const [enrollments, setEnrollments] = React.useState<Enrollment[]>([]);
  const [activeTreeId, setActiveTreeId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!user?.uid) return;
    enrollmentService.getByUser(user.uid).then((enrs) => {
      setEnrollments(enrs);
      if (enrs.length > 0 && !activeTreeId) setActiveTreeId(enrs[0].skillTreeId);
    }).catch(console.error);
  }, [user?.uid]);

  const activeEnrollment = enrollments.find((e) => e.skillTreeId === activeTreeId) || null;
  const activeTree = SKILL_TREES.find((tr) => tr.id === activeTreeId);
  const filteredQuals = activeTree
    ? qualifications.filter((q) => activeTree.qualificationIds.includes(q.id))
    : qualifications;

  const fmtDate = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold " style={{ color: t.textPrimary }}>My Skills</h1>
          <p className="mt-2 " style={{ color: t.textMuted }}>
            Track your progress through qualifications and certifications
          </p>
        </div>
        {enrollments.length > 1 && (
          <SkillTreeTabs
            enrollments={enrollments}
            activeTreeId={activeTreeId}
            onSelect={setActiveTreeId}
          />
        )}
        {activeEnrollment && (
          <div className="flex items-center gap-4 text-sm " style={{ color: t.textMuted }}>
            <span className="font-mono text-xs px-2.5 py-1 rounded-full border  ">
              Started {fmtDate(activeEnrollment.startDate)}
            </span>
          </div>
        )}
        <SkillTree
          qualifications={filteredQuals}
          userProgress={userProgress || {}}
          onUpdateProgress={(progress) => updateProgress(user!.uid, progress)}
          isInstructor={canEdit}
          enrollment={activeEnrollment}
        />
      </div>
    </Layout>
  );
};

// Separate component for student tree view to handle dynamic userId
const TreeViewRoute: React.FC = () => {
  usePageTitle('Training', 'Student Skills');
  const { t } = useTheme();
  const { user } = useAuth();
  const { userId } = useParams<{ userId: string }>();
  const { qualifications, allUsers, getProgress, updateProgress } = useData(userId);
  const [enrollments, setEnrollments] = React.useState<Enrollment[]>([]);
  const [activeTreeId, setActiveTreeId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!userId) return;
    enrollmentService.getByUser(userId).then((enrs) => {
      setEnrollments(enrs);
      if (enrs.length > 0 && !activeTreeId) setActiveTreeId(enrs[0].skillTreeId);
    }).catch(console.error);
  }, [userId]);

  if (!userId) {
    return (
      <Layout>
        <div className="" style={{ color: t.textMuted }}>Invalid user</div>
      </Layout>
    );
  }

  const userProgress = getProgress(userId);
  const studentName = user?.uid === userId
    ? 'Your'
    : (allUsers.find((u) => u.uid === userId)?.displayName ?? 'Student') + "'s";

  const activeEnrollment = enrollments.find((e) => e.skillTreeId === activeTreeId) || null;
  const activeTree = SKILL_TREES.find((tr) => tr.id === activeTreeId);
  const filteredQuals = activeTree
    ? qualifications.filter((q) => activeTree.qualificationIds.includes(q.id))
    : qualifications;

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold " style={{ color: t.textPrimary }}>{studentName} Skill Tree</h1>
          <p className="mt-2 " style={{ color: t.textMuted }}>Review and sign off on qualifications</p>
        </div>
        {enrollments.length > 1 && (
          <SkillTreeTabs
            enrollments={enrollments}
            activeTreeId={activeTreeId}
            onSelect={setActiveTreeId}
          />
        )}
        <SkillTree
          qualifications={filteredQuals}
          userProgress={userProgress || {}}
          onUpdateProgress={(progress) => updateProgress(userId, progress)}
          isInstructor={user?.role === 'instructor' || user?.role === 'admin'}
          enrollment={activeEnrollment}
        />
      </div>
    </Layout>
  );
};

const WaterWellTechTrainingRoute: React.FC = () => {
  usePageTitle('Training', 'Water Well Technician Training');
  return (
    <Layout>
      <WaterWellTechTraining />
    </Layout>
  );
};

const DashboardRoute: React.FC = () => {
  usePageTitle('Training', 'Dashboard');
  const { t } = useTheme();
  const { allUsers, qualifications, loadingUsers, qualificationsLoading } = useData(undefined);

  if (loadingUsers || qualificationsLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="" style={{ color: t.textMuted }}>Loading dashboard...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Dashboard users={allUsers} qualifications={qualifications} />
    </Layout>
  );
};

const UserManagementRoute: React.FC = () => {
  usePageTitle('Training', 'Users');
  const { allUsers } = useData(undefined);

  return (
    <Layout>
      <UserManagement users={allUsers} />
    </Layout>
  );
};

const SkillTreeConfigRoute: React.FC = () => {
  usePageTitle('Training', 'Skill Trees');
  return (
    <Layout>
      <SkillTreeConfig />
    </Layout>
  );
};

const LibraryListRoute: React.FC = () => {
  usePageTitle('Training', 'Library');
  return (
    <Layout>
      <LibraryPage />
    </Layout>
  );
};

const LibraryViewRoute: React.FC = () => {
  usePageTitle('Training', 'Article');
  return (
    <Layout>
      <LibraryItemView />
    </Layout>
  );
};

const LibraryEditorRoute: React.FC = () => {
  usePageTitle('Training', 'Edit Article');
  return (
    <Layout>
      <LibraryItemEditor />
    </Layout>
  );
};

const AttestationsRoute: React.FC = () => {
  usePageTitle('Training', 'Attestations');
  return (
    <Layout>
      <AttestationList />
    </Layout>
  );
};

const AttestationsDashboardRoute: React.FC = () => {
  usePageTitle('Training', 'Attestation Dashboard');
  return (
    <Layout>
      <AttestationDashboard />
    </Layout>
  );
};

// EnrollmentManagerRoute removed — absorbed into Dashboard

const MonitoringRoute: React.FC = () => {
  usePageTitle('Training', 'Monitoring');
  return (
    <Layout>
      <MonitoringDashboard />
    </Layout>
  );
};

const TimelineRoute: React.FC = () => {
  usePageTitle('Training', 'Timeline');
  return (
    <Layout>
      <StudentTimeline />
    </Layout>
  );
};

const App: React.FC = () => {
  React.useEffect(() => {
    seedDemoDB();
    monitoring.init();
  }, []);
  return (
    <Sentry.ErrorBoundary fallback={<div style={{ padding: '2rem' }}><AlertBanner variant="error" message="Something went wrong. Please refresh the page." persistent /></div>}>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <DataProvider>
              <AppRoutes />
            </DataProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
};

export default App;
